import type { UValueResult } from '../calculation/types';
import type { ConstructionTemplate } from '../data/catalog';
import { calculateLayerResistance } from '../calculation/calculate-u-value';

declare global {
  interface Window {
    jspdf?: {
      // The locally bundled, pinned jsPDF UMD build exposes this constructor.
      jsPDF: new (...args: any[]) => any;
    };
  }
}

async function loadImageAsDataUrl(url: string): Promise<string> {
  const response = await fetch(url);
  if (!response.ok) throw new Error('Could not load the Sisalwool logo.');
  const blob = await response.blob();
  return await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error('Could not read the Sisalwool logo.'));
    reader.readAsDataURL(blob);
  });
}

function safeFilename(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 60);
}

export async function downloadCalculationPdf(
  template: ConstructionTemplate,
  calculation: UValueResult,
  requestedBy: string,
): Promise<void> {
  const JsPdf = window.jspdf?.jsPDF;
  if (!JsPdf) throw new Error('The PDF generator is not available.');
  const pdf = new JsPdf({ unit: 'mm', format: 'a4', compress: true });
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 18;
  const contentWidth = pageWidth - margin * 2;
  let y = 18;

  const addPageIfNeeded = (requiredHeight: number) => {
    if (y + requiredHeight <= pageHeight - 18) return;
    pdf.addPage();
    y = 20;
  };

  const labelValue = (label: string, value: string) => {
    addPageIfNeeded(8);
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(9);
    pdf.setTextColor(95, 105, 108);
    pdf.text(label, margin, y);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(24, 36, 37);
    pdf.text(value, pageWidth - margin, y, { align: 'right' });
    y += 7;
  };

  try {
    const logo = await loadImageAsDataUrl('/assets/sisalwool-logo.png');
    pdf.addImage(logo, 'PNG', margin, y, 58, 10.3);
  } catch {
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(20);
    pdf.setTextColor(24, 36, 37);
    pdf.text('SISALWOOL', margin, y + 8);
  }

  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(9);
  pdf.setTextColor(95, 105, 108);
  pdf.text('Indicative U-value calculation', pageWidth - margin, y + 7, { align: 'right' });
  y += 17;
  pdf.setDrawColor(245, 108, 102);
  pdf.setLineWidth(1.2);
  pdf.line(margin, y, pageWidth - margin, y);
  y += 14;

  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(23);
  pdf.setTextColor(24, 36, 37);
  pdf.text('U-value calculation summary', margin, y);
  y += 9;
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(10);
  pdf.setTextColor(58, 75, 85);
  pdf.text(template.name, margin, y);
  y += 12;

  pdf.setFillColor(24, 36, 37);
  pdf.roundedRect(margin, y, contentWidth, 34, 4, 4, 'F');
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(9);
  pdf.setTextColor(197, 214, 210);
  pdf.text('INDICATIVE U-VALUE', margin + 7, y + 9);
  pdf.setFontSize(25);
  pdf.setTextColor(255, 255, 255);
  pdf.text(calculation.displayedUValueWm2K.toFixed(2), margin + 7, y + 25);
  pdf.setFontSize(11);
  pdf.setTextColor(197, 214, 210);
  pdf.text('W/m2K', margin + 37, y + 25);
  pdf.setFontSize(8.5);
  pdf.text(`Mean resistance ${calculation.meanResistanceM2KW.toFixed(3)} m2K/W`, pageWidth - margin - 7, y + 20, { align: 'right' });
  y += 46;

  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(14);
  pdf.setTextColor(24, 36, 37);
  pdf.text('Calculation details', margin, y);
  y += 9;
  labelValue('Upper resistance limit', `${calculation.upperResistanceM2KW.toFixed(3)} m2K/W`);
  labelValue('Lower resistance limit', `${calculation.lowerResistanceM2KW.toFixed(3)} m2K/W`);
  labelValue('Mean thermal resistance', `${calculation.meanResistanceM2KW.toFixed(3)} m2K/W`);
  labelValue('Unrounded U-value', `${calculation.finalUValueWm2K.toFixed(5)} W/m2K`);
  labelValue('Applied corrections', `${calculation.correctionsAppliedWm2K.toFixed(3)} W/m2K`);
  y += 5;

  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(14);
  pdf.text('Construction layers', margin, y);
  y += 7;

  template.layers.forEach((layer, index) => {
    addPageIfNeeded(19);
    pdf.setDrawColor(225, 227, 226);
    pdf.setFillColor(index % 2 === 0 ? 249 : 255, index % 2 === 0 ? 249 : 255, index % 2 === 0 ? 247 : 255);
    pdf.roundedRect(margin, y, contentWidth, 16, 2, 2, 'FD');
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(9.5);
    pdf.setTextColor(24, 36, 37);
    pdf.text(`${index + 1}. ${layer.name}`, margin + 4, y + 6);
    const details = [
      layer.thicknessMm !== undefined ? `${layer.thicknessMm} mm` : null,
      layer.conductivityWmK !== undefined ? `lambda ${layer.conductivityWmK.toFixed(3)} W/mK` : null,
      `R ${calculateLayerResistance(layer).toFixed(3)} m2K/W`,
    ].filter(Boolean).join('  |  ');
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(8);
    pdf.setTextColor(95, 105, 108);
    pdf.text(details, margin + 4, y + 12);
    y += 19;
  });

  if (template.bridge) {
    addPageIfNeeded(27);
    y += 2;
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(12);
    pdf.setTextColor(24, 36, 37);
    pdf.text('Repeating timber bridge', margin, y);
    y += 7;
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(9);
    pdf.setTextColor(58, 75, 85);
    pdf.text(
      `${template.bridge.memberName}: ${template.bridge.widthMm} mm at ${template.bridge.spacingMm} mm centres (${((template.bridge.widthMm / template.bridge.spacingMm) * 100).toFixed(1)}% area).`,
      margin,
      y,
    );
    y += 11;
  }

  addPageIfNeeded(39);
  pdf.setFillColor(255, 248, 233);
  pdf.setDrawColor(234, 216, 186);
  pdf.roundedRect(margin, y, contentWidth, 27, 3, 3, 'FD');
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(9);
  pdf.setTextColor(106, 72, 39);
  pdf.text('Important', margin + 5, y + 7);
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(8);
  const disclaimer = pdf.splitTextToSize(
    'This is an indicative calculation based on the values entered. Verify product values, assumptions and regulatory compliance with a suitably qualified professional. It is not a certificate of compliance.',
    contentWidth - 10,
  );
  pdf.text(disclaimer, margin + 5, y + 13);
  y += 34;

  addPageIfNeeded(46);
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(13);
  pdf.setTextColor(24, 36, 37);
  pdf.text('Contact Sisalwool', margin, y);
  y += 7;
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(9);
  pdf.setTextColor(58, 75, 85);
  pdf.text('Midlothian Innovation Centre, Roslin, UK, EH25 9RE', margin, y);
  y += 6;
  pdf.text('Telephone: 0131 510 9296', margin, y);
  y += 6;
  pdf.text('Email: info@sisalwool.com', margin, y);
  y += 6;
  pdf.text('Website: www.sisalwool.com', margin, y);
  y += 11;

  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(8);
  pdf.setTextColor(115, 123, 131);
  pdf.text(`Requested by ${requestedBy} on ${new Date().toLocaleDateString('en-GB')}`, margin, y);
  pdf.text('Indicative calculation - verify before specification', pageWidth - margin, y, { align: 'right' });

  pdf.save(`sisalwool-u-value-${safeFilename(template.name) || 'calculation'}.pdf`);
}
